import { useState, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import {
  ArrowRight,
  CheckCircle,
  Circle,
  Download,
  ExternalLink,
  Eye,
  EyeOff,
  FileText,
  FileUp,
  Loader2,
  Mail,
  Pencil,
  SlidersHorizontal,
  Trash2,
  Upload,
  User,
} from "lucide-react";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { LoadingPage } from "@/components/shared/LoadingPage";
import { PageHeader } from "@/components/shared/PageHeader";
import { StatusBadge, type StatusBadgeVariant } from "@/components/shared/StatusBadge";
import { ConfirmationDialog } from "@/components/shared/ConfirmationDialog";
import { apiBaseUrl } from "@/services/apiClient";
import { TenantFiscalProfileCard } from "@/pages/tenants/TenantFiscalProfileCard";
import { TenantOnboardingAutoFlowCard } from "@/pages/tenants/TenantOnboardingAutoFlowCard";
import { TenantFiscaloConfigCard } from "@/pages/tenants/TenantFiscaloConfigCard";
import { TenantHomologacionCard } from "@/pages/tenants/TenantHomologacionCard";
import { TenantBajaCard } from "@/pages/tenants/TenantBajaCard";
import { tenantKeys } from "@/lib/queryKeys";
import {
  getTenantOnboardingOverview,
  listTenantDocuments,
  uploadTenantDocumentPlatform,
  updateTenantDocumentNamePlatform,
  deleteTenantDocumentPlatform,
  listSupportMessages,
  createSupportMessage,
  updateTenant,
  deleteInactiveSetupTenant,
  getDgiCredentials,
  uploadDgiCredentials,
  removeDgiCredentials,
  downloadDgiCertificate,
  revealDgiPassphrase,
  downloadExportEmittedCfesForTenant,
  downloadExportRecibidasCfesForTenant,
  type TenantOnboardingOverview,
  type PlatformTenantStatus,
} from "@/services/platformTenantsService";
import { DGI_CERTIFICATE_FILE_ACCEPT } from "@/services/authService";
import { DGI_CREDENTIALS_ERROR_CODES } from "@/lib/dgiCredentialErrorCodes";
import { PasswordInput } from "@/components/ui/password-input";
import { useAuth } from "@/contexts/AuthContext";
import { useTenantSwitcher } from "@/contexts/TenantSwitcherContext";
import { formatDisplayDate } from "@/lib/dateFormat";
import { ImportEmittedCfeDialog } from "@/pages/tenants/ImportEmittedCfeDialog";
import { ImportRecibidasCfeDialog } from "@/pages/tenants/ImportRecibidasCfeDialog";
import { TenantCaeCard } from "@/pages/tenants/TenantCaeCard";
import { statusBadgeVariant } from "@/pages/tenants/tenantStatusBadge";

/*
 * Was a local Record duplicating the mapping tenants-page and
 * TenantMobileCard each had their own copy of. Three copies meant a new status
 * had to be added in three places, with nothing to catch a miss — the shared
 * helper is typed against PlatformTenantStatus, so an unhandled status is a
 * compile error.
 *
 * NOTE this page rendered pending_approval as "info" while the other two used
 * "warning". Unified on the shared mapping; if the softer tone here was
 * deliberate, it was undocumented and invisible next to the list it links from.
 */

const CERTIFICATE_REQUEST_STATUS_KEY: Record<string, string> = {
  pending: "certificateRequests.page.statusPending",
  in_progress: "certificateRequests.page.statusInProgress",
  fulfilled: "certificateRequests.page.statusFulfilled",
  failed: "certificateRequests.page.statusFailed",
};

const CERTIFICATE_REQUEST_STATUS_VARIANT: Record<string, StatusBadgeVariant> = {
  pending: "warning",
  in_progress: "info",
  fulfilled: "success",
  failed: "danger",
};

function OnboardingChecklist({
  overview,
}: {
  overview: TenantOnboardingOverview;
}): JSX.Element {
  const { t } = useTranslation();
  const s1Complete =
    (overview.address?.trim() ?? "") !== "" &&
    (overview.city?.trim() ?? "") !== "" &&
    (overview.department?.trim() ?? "") !== "" &&
    (overview.phone?.trim() ?? "") !== "" &&
    ((overview.industry?.trim() ?? "") !== "" ||
      (overview.profession?.trim() ?? "") !== "");
  const s3Complete = Boolean(
    (overview.legalName?.trim() ?? "") !== "" ||
      overview.profileImage ||
      overview.invoiceLogoImage
  );
  const s4Complete = overview.hasDgiCredentials;
  const literalEComplete =
    overview.literalE === undefined ||
    overview.literalE === null ||
    overview.literalE === false;
  const hasPlan = Boolean(overview.planId);

  const steps: Array<{ key: string; done: boolean }> = [
    {
      key: "tenants.detail.onboardingSteps.certificate",
      done: overview.certificateRequest != null || overview.step6AbitabAcknowledgedAt != null,
    },
    { key: "tenants.detail.onboardingSteps.dgiCredentials", done: s4Complete },
    { key: "tenants.detail.onboardingSteps.legalData", done: s1Complete },
    { key: "tenants.detail.onboardingSteps.literalE", done: literalEComplete },
    { key: "tenants.detail.onboardingSteps.branding", done: s3Complete },
    { key: "tenants.detail.onboardingSteps.plan", done: hasPlan },
  ];

  return (
    <ul className="space-y-2">
      {steps.map(({ key, done }) => (
        <li key={key} className="flex items-center gap-2 text-sm">
          {done ? (
            <CheckCircle className="h-4 w-4 shrink-0 text-green-600" />
          ) : (
            <Circle className="h-4 w-4 shrink-0 text-muted-foreground" />
          )}
          <span className={done ? "text-foreground" : "text-muted-foreground"}>
            {t(key)}
          </span>
        </li>
      ))}
    </ul>
  );
}

const EXPIRING_DAYS = 30;
function isCertExpired(validTo: string | undefined): boolean {
  if (!validTo) return false;
  return new Date(validTo) < new Date();
}
function isCertExpiringSoon(validTo: string | undefined): boolean {
  if (!validTo) return false;
  const end = new Date(validTo);
  const in30 = new Date();
  in30.setDate(in30.getDate() + EXPIRING_DAYS);
  return end >= new Date() && end <= in30;
}

function DgiCertificateCard({
  tenantId,
  rut,
  onSuccess,
}: {
  tenantId: string;
  rut: string;
  onSuccess: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  const p12InputRef = useRef<HTMLInputElement>(null);
  const [p12File, setP12File] = useState<File | null>(null);
  const [dgiPassphrase, setDgiPassphrase] = useState("");
  const [showDgiReplaceForm, setShowDgiReplaceForm] = useState(false);
  const [dgiUploading, setDgiUploading] = useState(false);
  const [dgiRemoving, setDgiRemoving] = useState(false);
  const [dgiDownloading, setDgiDownloading] = useState(false);
  const [revealedPassphrase, setRevealedPassphrase] = useState<string | null>(null);
  const [passphraseRevealing, setPassphraseRevealing] = useState(false);

  const { data: dgiCredentials, isLoading: dgiCredentialsLoading, refetch: refetchDgi } = useQuery({
    queryKey: [...tenantKeys.overview(tenantId), "dgi-credentials"] as const,
    queryFn: () => getDgiCredentials(tenantId),
    enabled: Boolean(tenantId),
  });

  const loadDgiCredentials = (): void => {
    void refetchDgi();
  };

  const handleDgiUpload = async (): Promise<void> => {
    if (!tenantId || !p12File || !rut) return;
    const trimmedPassphrase = (dgiPassphrase ?? "").trim();
    if (!trimmedPassphrase) {
      toast.error(t("tenants.edit.dgi.passphraseRequired"));
      return;
    }
    setDgiUploading(true);
    try {
      // Default IdEmisor = 1 (fresh DGI sequence). Tenants migrating from another
      // provider need their last-used IdEmisor + 1 instead; that lookup will land
      // with the DGI-portal automation.
      await uploadDgiCredentials(tenantId, p12File, 1, trimmedPassphrase);
      setP12File(null);
      setDgiPassphrase("");
      if (p12InputRef.current) p12InputRef.current.value = "";
      loadDgiCredentials();
      onSuccess();
      toast.success(t("tenants.edit.success"));
    } catch (err) {
      const data =
        err && typeof err === "object" && "response" in err
          ? (err as { response?: { data?: { errorCode?: string; message?: string } } }).response?.data
          : undefined;
      const errorCode = data?.errorCode;
      const messageByCode: Record<string, string> = {
        [DGI_CREDENTIALS_ERROR_CODES.CERTIFICATE_RUC_MISMATCH]: t(
          "tenants.edit.dgi.certificateRucMismatch",
          { lng: "es" }
        ),
        [DGI_CREDENTIALS_ERROR_CODES.CERTIFICATE_NO_RUC]: t(
          "tenants.edit.dgi.certificateNoRuc",
          { lng: "es" }
        ),
      };
      const translated = errorCode ? messageByCode[errorCode] : null;
      toast.error(translated ?? t("tenants.edit.dgi.errorUpload", { lng: "es" }));
    } finally {
      setDgiUploading(false);
    }
  };

  const handleDgiRemove = async (): Promise<void> => {
    if (!tenantId) return;
    if (!window.confirm(t("tenants.edit.dgi.removeConfirm"))) return;
    setDgiRemoving(true);
    try {
      await removeDgiCredentials(tenantId);
      loadDgiCredentials();
      onSuccess();
      toast.success(t("tenants.edit.success"));
    } catch {
      toast.error(t("tenants.edit.dgi.errorRemove"));
    } finally {
      setDgiRemoving(false);
    }
  };

  const handleDgiDownload = async (): Promise<void> => {
    if (!tenantId) return;
    setDgiDownloading(true);
    try {
      const { blob, filename } = await downloadDgiCertificate(tenantId);
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error(t("tenants.edit.dgi.errorDownload", { lng: "es" }));
    } finally {
      setDgiDownloading(false);
    }
  };

  const handleTogglePassphrase = async (): Promise<void> => {
    if (revealedPassphrase !== null) {
      setRevealedPassphrase(null);
      return;
    }
    if (!tenantId) return;
    setPassphraseRevealing(true);
    try {
      const { passphrase } = await revealDgiPassphrase(tenantId);
      setRevealedPassphrase(passphrase ?? "");
    } catch {
      toast.error(t("tenants.edit.dgi.errorReveal", { lng: "es" }));
    } finally {
      setPassphraseRevealing(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{t("tenants.edit.dgi.title")}</CardTitle>
        <p className="text-sm text-muted-foreground mt-1">
          {t("tenants.edit.dgi.description")}
        </p>
      </CardHeader>
      <CardContent>
        {dgiCredentialsLoading ? (
          <p className="text-sm text-muted-foreground">{t("tenants.page.loading")}</p>
        ) : dgiCredentials?.hasCredentials && !showDgiReplaceForm ? (
          <div className="space-y-3">
            <p className="text-sm">
              {t("tenants.edit.dgi.configured")}
              {dgiCredentials.certificateValidFrom && dgiCredentials.certificateValidTo && (
                <span className="text-muted-foreground block mt-1">
                  {t("tenants.edit.dgi.validFromTo", {
                    from: dgiCredentials.certificateValidFrom,
                    to: dgiCredentials.certificateValidTo,
                  })}
                </span>
              )}
            </p>
            {dgiCredentials.certificateValidTo &&
              isCertExpired(dgiCredentials.certificateValidTo) && (
                <p className="text-sm text-destructive">{t("tenants.edit.dgi.expired")}</p>
              )}
            {dgiCredentials.certificateValidTo &&
              !isCertExpired(dgiCredentials.certificateValidTo) &&
              isCertExpiringSoon(dgiCredentials.certificateValidTo) && (
                <p className="text-sm text-amber-600 dark:text-amber-500">
                  {t("tenants.edit.dgi.expiringSoon")}
                </p>
              )}
            <div className="grid gap-2">
              <Label>{t("tenants.edit.dgi.passphraseLabel")}</Label>
              <div className="flex items-center gap-2">
                <code className="flex-1 rounded-md bg-muted px-3 py-2 text-sm font-mono">
                  {revealedPassphrase === null
                    ? "••••••••"
                    : revealedPassphrase === ""
                      ? t("tenants.edit.dgi.noPassphrase", { lng: "es" })
                      : revealedPassphrase}
                </code>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => void handleTogglePassphrase()}
                  disabled={passphraseRevealing}
                >
                  {passphraseRevealing ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : revealedPassphrase !== null ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                  <span className="ml-1.5">
                    {revealedPassphrase !== null
                      ? t("tenants.edit.dgi.hide", { lng: "es" })
                      : t("tenants.edit.dgi.reveal", { lng: "es" })}
                  </span>
                </Button>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => void handleDgiDownload()}
                disabled={dgiDownloading}
              >
                {dgiDownloading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Download className="h-4 w-4" />
                )}
                <span className="ml-1.5">{t("tenants.edit.dgi.download", { lng: "es" })}</span>
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setShowDgiReplaceForm(true)}
                disabled={dgiRemoving}
              >
                {t("tenants.edit.dgi.replace")}
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => void handleDgiRemove()}
                disabled={dgiRemoving}
              >
                {dgiRemoving ? t("tenants.edit.dgi.removing") : t("tenants.edit.dgi.remove")}
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <input
              ref={p12InputRef}
              type="file"
              accept={DGI_CERTIFICATE_FILE_ACCEPT}
              className="hidden"
              onChange={(e) => setP12File(e.target.files?.[0] ?? null)}
              disabled={dgiUploading}
            />
            <div className="grid gap-2">
              <Label>{t("tenants.edit.dgi.p12Label")}</Label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => p12InputRef.current?.click()}
                disabled={dgiUploading}
              >
                {p12File ? p12File.name : t("tenants.edit.dgi.p12Accept")}
              </Button>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="dgi-passphrase-detail">{t("tenants.edit.dgi.passphraseLabel")}</Label>
              <PasswordInput
                id="dgi-passphrase-detail"
                placeholder={t("tenants.edit.dgi.passphrasePlaceholder")}
                value={dgiPassphrase}
                onChange={(e) => setDgiPassphrase(e.target.value)}
                disabled={dgiUploading}
                autoComplete="off"
              />
            </div>
            <div className="flex gap-2">
              <Button
                type="button"
                size="sm"
                onClick={() => void handleDgiUpload()}
                disabled={dgiUploading || !p12File || !dgiPassphrase?.trim()}
              >
                {dgiUploading
                  ? t("tenants.edit.dgi.uploading")
                  : dgiCredentials?.hasCredentials
                    ? t("tenants.edit.dgi.replace")
                    : t("tenants.edit.dgi.upload")}
              </Button>
              {showDgiReplaceForm && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setShowDgiReplaceForm(false);
                    setP12File(null);
                    setDgiPassphrase("");
                    if (p12InputRef.current) p12InputRef.current.value = "";
                  }}
                  disabled={dgiUploading}
                >
                  {t("tenants.edit.cancel")}
                </Button>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

const DOCUMENT_ACCEPT =
  ".pdf,image/jpeg,image/png,image/webp,text/plain,text/csv,.csv,.doc,.docx,.xls,.xlsx,.odt,.ods,.rtf";

const TAB_FACTURA = "factura-electronica";
const TAB_FISCAL = "fiscal-profile";
const TAB_GENERAL = "general";

export function TenantDetailPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { impersonate } = useTenantSwitcher();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const tabParam = searchParams.get("tab");
  const tab =
    tabParam === TAB_FACTURA
      ? TAB_FACTURA
      : tabParam === TAB_FISCAL
        ? TAB_FISCAL
        : TAB_GENERAL;
  const setTab = (value: string): void => {
    setSearchParams(value === TAB_GENERAL ? {} : { tab: value });
  };

  const [renameDocId, setRenameDocId] = useState<string | null>(null);
  const [renameDocName, setRenameDocName] = useState("");
  const [deleteDocId, setDeleteDocId] = useState<string | null>(null);
  const [supportMessageText, setSupportMessageText] = useState("");
  const [importEmittedOpen, setImportEmittedOpen] = useState(false);
  const [importRecibidasOpen, setImportRecibidasOpen] = useState(false);
  const [deleteInactiveOpen, setDeleteInactiveOpen] = useState(false);
  const [exportEmittedLoading, setExportEmittedLoading] = useState(false);
  const [exportRecibidasLoading, setExportRecibidasLoading] = useState(false);
  const [showDgiPassword, setShowDgiPassword] = useState(false);

  const { data: overview, isLoading, isError } = useQuery({
    queryKey: tenantKeys.overview(id ?? ""),
    queryFn: () => getTenantOnboardingOverview(id!),
    enabled: Boolean(id),
  });

  const { data: documents = [], isLoading: documentsLoading } = useQuery({
    queryKey: tenantKeys.documents(id ?? ""),
    queryFn: () => listTenantDocuments(id!),
    enabled: Boolean(id),
  });

  const uploadDocMutation = useMutation({
    mutationFn: (file: File) =>
      uploadTenantDocumentPlatform(id!, file, file.name),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tenantKeys.documents(id!) });
      toast.success(t("tenants.detail.documents.uploadSuccess"));
      if (fileInputRef.current) fileInputRef.current.value = "";
    },
    onError: () => toast.error(t("tenants.detail.documents.uploadError")),
  });

  const renameDocMutation = useMutation({
    mutationFn: ({ docId, name }: { docId: string; name: string }) =>
      updateTenantDocumentNamePlatform(id!, docId, name),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tenantKeys.documents(id!) });
      setRenameDocId(null);
      setRenameDocName("");
      toast.success(t("tenants.detail.documents.renameSuccess"));
    },
    onError: () => toast.error(t("tenants.detail.documents.renameError")),
  });

  const deleteDocMutation = useMutation({
    mutationFn: (docId: string) => deleteTenantDocumentPlatform(id!, docId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tenantKeys.documents(id!) });
      setDeleteDocId(null);
      toast.success(t("tenants.detail.documents.deleteSuccess"));
    },
    onError: () => toast.error(t("tenants.detail.documents.deleteError")),
  });

  const { data: supportMessages = [], isLoading: supportMessagesLoading } =
    useQuery({
      queryKey: tenantKeys.supportMessages(id ?? ""),
      queryFn: () => listSupportMessages(id!),
      enabled: Boolean(id),
    });

  const { data: dgiCredentials, isLoading: dgiCredentialsLoading } = useQuery({
    queryKey: [...tenantKeys.overview(id ?? ""), "dgi-credentials"] as const,
    queryFn: () => getDgiCredentials(id!),
    enabled: Boolean(id),
  });

  const sendSupportMessageMutation = useMutation({
    mutationFn: (message: string) => createSupportMessage(id!, message),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: tenantKeys.supportMessages(id!),
      });
      setSupportMessageText("");
      toast.success(t("tenants.detail.support.sendSuccess"));
    },
    onError: () => toast.error(t("tenants.detail.support.sendError")),
  });

  const setDgiEnvMutation = useMutation({
    mutationFn: ({
      tenantId,
      dgiEnv,
    }: {
      tenantId: string;
      dgiEnv: "test" | "prod" | null;
    }) => updateTenant(tenantId, { dgiEnv }),
    onSuccess: (_, { tenantId }) => {
      queryClient.invalidateQueries({ queryKey: tenantKeys.overview(tenantId) });
      toast.success(t("tenants.edit.success"));
    },
    onError: () => toast.error(t("tenants.page.error")),
  });

  const deleteInactiveMutation = useMutation({
    mutationFn: () => deleteInactiveSetupTenant(id!),
    onMutate: () => {
      toast.loading(t("tenants.detail.deleteInactiveSetup.loading"), {
        id: "tenant-delete-inactive",
      });
    },
    onSuccess: () => {
      toast.success(t("tenants.detail.deleteInactiveSetup.success"), {
        id: "tenant-delete-inactive",
      });
      setDeleteInactiveOpen(false);
      void queryClient.invalidateQueries({ queryKey: tenantKeys.all });
      navigate("/tenants");
    },
    onError: () => {
      toast.error(t("tenants.detail.deleteInactiveSetup.error"), {
        id: "tenant-delete-inactive",
      });
    },
  });

  const handleImpersonate = (): void => {
    if (!overview) return;
    void impersonate(overview.id, overview.legalName?.trim() ?? overview.name).then(
      () => {
        navigate("/dashboard", { replace: true });
      }
    );
  };

  if (!id) {
    return (
      <div className="p-6 text-sm text-muted-foreground">
        {t("tenants.detail.notFound")}
      </div>
    );
  }

  if (isLoading || !overview) {
    return <LoadingPage />;
  }

  if (isError) {
    return (
      <div className="p-6">
        <PageHeader
          title={t("tenants.detail.title")}
          onBack={() => navigate("/tenants")}
        />
        <p className="mt-4 text-sm text-destructive">{t("tenants.detail.errorLoad")}</p>
      </div>
    );
  }

  const displayName = overview.legalName?.trim() ?? overview.name;
  const profileImageUrl =
    overview.profileImage != null && overview.profileImage !== ""
      ? overview.profileImage.startsWith("http")
        ? overview.profileImage
        : `${apiBaseUrl}${overview.profileImage}`
      : null;

  return (
    <div className="space-y-4 p-6">
      <PageHeader
        title={displayName}
        description={t("tenants.detail.description", { rut: overview.rut })}
        onBack={() => navigate("/tenants")}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" size="sm" asChild>
              <Link to={`/tenants/${id}/edit`} state={{ from: "tenant-detail" }}>
                <Pencil className="mr-2 h-4 w-4" />
                {t("tenants.detail.editCompany")}
              </Link>
            </Button>
            {user?.platformRole === "root" && overview.status === "onboarding" && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="border-destructive/40 text-destructive transition-colors hover:bg-destructive/10"
                onClick={() => setDeleteInactiveOpen(true)}
              >
                <Trash2 className="mr-2 h-4 w-4" />
                {t("tenants.detail.deleteInactiveSetup.button")}
              </Button>
            )}
            <Button size="sm" onClick={handleImpersonate}>
              <ArrowRight className="mr-2 h-4 w-4" />
              {t("tenants.page.actions.impersonate")}
            </Button>
          </div>
        }
      />

      <div className="flex flex-wrap items-center gap-3">
        <Avatar className="h-12 w-12">
          {profileImageUrl ? (
            <AvatarImage src={profileImageUrl} alt="" />
          ) : null}
          <AvatarFallback className="text-lg bg-primary text-primary-foreground">
            {displayName.charAt(0)}
          </AvatarFallback>
        </Avatar>
        <StatusBadge variant={statusBadgeVariant(overview.status)}>
          {overview.status === "active"
            ? t("tenants.page.statusActive")
            : overview.status === "onboarding"
              ? t("tenants.page.statusOnboarding")
              : overview.status === "pending_approval"
                ? t("tenants.page.statusPendingApproval")
                : t("tenants.page.statusPendingDeletion")}
        </StatusBadge>
        <StatusBadge variant="info">
          {overview.dgiEnv === "prod"
            ? t("tenants.edit.dgiEnvProd")
            : overview.dgiEnv === "test"
              ? t("tenants.edit.dgiEnvTest")
              : t("tenants.page.dgiEnvSystem")}
        </StatusBadge>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="space-y-4">
        <TabsList className="gap-2">
          <TabsTrigger value={TAB_GENERAL}>
            {t("tenants.detail.tabs.general")}
          </TabsTrigger>
          <TabsTrigger value={TAB_FACTURA}>
            {t("tenants.detail.tabs.facturaElectronica")}
          </TabsTrigger>
          <TabsTrigger value={TAB_FISCAL}>Perfil fiscal</TabsTrigger>
        </TabsList>
        <TabsContent value={TAB_GENERAL} className="space-y-4 mt-4">
          <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">{t("tenants.detail.companyInfo")}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <p>
              <span className="font-medium text-muted-foreground">
                {t("tenants.page.legalName")}:{" "}
              </span>
              {overview.legalName?.trim() ?? "—"}
            </p>
            <p>
              <span className="font-medium text-muted-foreground">
                {t("tenants.page.rut")}:{" "}
              </span>
              {overview.rut}
            </p>
            <p>
              <span className="font-medium text-muted-foreground">
                {t("tenants.detail.address")}:{" "}
              </span>
              {overview.address?.trim() ?? "—"}
            </p>
            <p>
              <span className="font-medium text-muted-foreground">
                {t("tenants.detail.phone")}:{" "}
              </span>
              {overview.phone?.trim() ?? "—"}
            </p>
            <p className="flex items-center gap-2 flex-wrap">
              <span className="font-medium text-muted-foreground">
                {t("tenants.detail.industry")}:{" "}
              </span>
              {(overview.industry?.trim() ?? overview.profession?.trim()) ? (
                (() => {
                  const raw =
                    overview.industry?.trim() ?? overview.profession?.trim() ?? "";
                  const labelKey = `onboarding.industryOptions.${raw}`;
                  const label = t(labelKey);
                  return (
                    <StatusBadge
                      variant="info"
                      className="font-normal"
                    >
                      {label === labelKey ? raw : label}
                    </StatusBadge>
                  );
                })()
              ) : (
                "—"
              )}
            </p>
          </CardContent>
        </Card>

        {overview.status === "onboarding" && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">
                {t("tenants.detail.onboardingProgress")}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <OnboardingChecklist overview={overview} />
            </CardContent>
          </Card>
        )}
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <CardTitle className="flex items-center gap-2 text-base">
            <FileText className="h-4 w-4" />
            {t("tenants.detail.dgiCredentials")}
          </CardTitle>
          <Button variant="outline" size="sm" asChild>
            <a
              href="https://www.efactura.dgi.gub.uy/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2"
            >
              <ExternalLink className="h-4 w-4" />
              {t("tenants.detail.dgiCredentialsOpenPortal")}
            </a>
          </Button>
        </CardHeader>
        <CardContent>
          {dgiCredentialsLoading ? (
            <p className="text-sm text-muted-foreground">
              {t("tenants.page.loading")}
            </p>
          ) : (
            <div className="space-y-4 text-sm">
              {/* Certificate (P12) section — always rendered so root sees an
                  explicit status either way, not a silent omission. */}
              <div className="space-y-2">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  {t(
                    "tenants.detail.dgiCertificateSubheading",
                    "Certificado digital",
                  )}
                </p>
                {dgiCredentials?.hasCredentials ? (
                  <>
                    <p className="text-foreground">
                      {t("tenants.detail.dgiCredentialsPresent")}
                      {dgiCredentials.certificateValidFrom &&
                        dgiCredentials.certificateValidTo && (
                          <span className="text-muted-foreground block mt-1">
                            {t("tenants.detail.dgiCredentialsValidFromTo", {
                              from: dgiCredentials.certificateValidFrom,
                              to: dgiCredentials.certificateValidTo,
                            })}
                          </span>
                        )}
                    </p>
                    {dgiCredentials.certificateValidTo &&
                      isCertExpired(dgiCredentials.certificateValidTo) && (
                        <p className="text-destructive">
                          {t("tenants.detail.dgiCredentialsExpired")}
                        </p>
                      )}
                    {dgiCredentials.certificateValidTo &&
                      !isCertExpired(dgiCredentials.certificateValidTo) &&
                      isCertExpiringSoon(dgiCredentials.certificateValidTo) && (
                        <p className="text-amber-600 dark:text-amber-500">
                          {t("tenants.detail.dgiCredentialsExpiringSoon")}
                        </p>
                      )}
                  </>
                ) : (
                  <p className="text-muted-foreground">
                    {t(
                      "tenants.detail.dgiCertificateMissing",
                      "Sin certificado digital configurado.",
                    )}
                  </p>
                )}
              </div>

              {/* e-Factura portal user — same treatment: always rendered with
                  an explicit configured / missing message so root can see at
                  a glance whether both pieces are in place. */}
              <div className="space-y-2 border-t pt-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  {t(
                    "tenants.detail.dgiPortalUserSubheading",
                    "Usuario e-Factura",
                  )}
                </p>
                {overview.hasDgiPortalUser && overview.dgiPortalUser ? (
                  <>
                    <p className="text-foreground">
                      {t("tenants.detail.dgiPortalUserConfigured")}
                    </p>
                    <p className="text-muted-foreground">
                      {t("tenants.detail.dgiPortalUserDocument", {
                        documentType: overview.dgiPortalUser.documentType,
                        documentNumber: overview.dgiPortalUser.documentNumber,
                      })}
                    </p>
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-muted-foreground">
                        {t("tenants.detail.dgiPortalUserPasswordLabel")}:
                      </span>
                      <span className="font-mono text-foreground">
                        {showDgiPassword
                          ? overview.dgiPortalUser.password
                          : "••••••••"}
                      </span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-8 px-2 text-muted-foreground hover:text-foreground"
                        onClick={() => setShowDgiPassword((v) => !v)}
                        aria-label={
                          showDgiPassword
                            ? t("tenants.detail.dgiPortalUserPasswordHide")
                            : t("tenants.detail.dgiPortalUserPasswordShow")
                        }
                      >
                        {showDgiPassword ? (
                          <EyeOff className="h-4 w-4" />
                        ) : (
                          <Eye className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                    <p className="text-muted-foreground text-xs">
                      {t("tenants.detail.dgiPortalUserCreatedAt", {
                        date: formatDisplayDate(
                          overview.dgiPortalUser.createdAt,
                        ),
                      })}
                    </p>
                  </>
                ) : overview.hasDgiPortalUser ? (
                  <p className="text-foreground">
                    {t("tenants.detail.dgiPortalUserConfigured")}
                  </p>
                ) : (
                  <p className="text-muted-foreground">
                    {t(
                      "tenants.detail.dgiPortalUserMissing",
                      "Sin usuario e-Factura configurado.",
                    )}
                  </p>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {overview.certificateRequest && (
        <div className="grid gap-4 md:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">
                {t("tenants.detail.certificateRequest")}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <p className="flex items-center gap-2 flex-wrap">
                <span className="font-medium text-muted-foreground">
                  {t("tenants.detail.status")}:{" "}
                </span>
                <StatusBadge
                  variant={
                    CERTIFICATE_REQUEST_STATUS_VARIANT[
                      overview.certificateRequest.status
                    ] ?? "info"
                  }
                  className="font-normal"
                >
                  {t(
                    CERTIFICATE_REQUEST_STATUS_KEY[
                      overview.certificateRequest.status
                    ] ?? overview.certificateRequest.status
                  )}
                </StatusBadge>
              </p>
              {overview.certificateRequest.idDigitalRequestNumber && (
                <p>
                  <span className="font-medium text-muted-foreground">
                    {t("tenants.detail.requestNumber")}:{" "}
                  </span>
                  {overview.certificateRequest.idDigitalRequestNumber}
                </p>
              )}
              <p className="text-muted-foreground">
                {formatDisplayDate(overview.certificateRequest.createdAt)}
              </p>
              <Button variant="outline" size="sm" asChild>
                <Link to={`/certificate-requests/${overview.certificateRequest.id}`}>
                  {t("tenants.detail.viewCertificateRequest")}
                </Link>
              </Button>
            </CardContent>
          </Card>
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <FileText className="h-4 w-4" />
            {t("tenants.detail.documents.title")}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <input
              ref={fileInputRef}
              type="file"
              accept={DOCUMENT_ACCEPT}
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) uploadDocMutation.mutate(file);
              }}
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadDocMutation.isPending}
            >
              {uploadDocMutation.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Upload className="mr-2 h-4 w-4" />
              )}
              {t("tenants.detail.documents.upload")}
            </Button>
          </div>
          {documentsLoading ? (
            <p className="text-sm text-muted-foreground">
              {t("tenants.detail.documents.loading")}
            </p>
          ) : documents.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              {t("tenants.detail.documents.empty")}
            </p>
          ) : (
            <ul className="space-y-2">
              {documents.map((doc) => (
                <li
                  key={doc.id}
                  className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border bg-muted/30 px-3 py-2 text-sm"
                >
                  <div className="min-w-0 flex-1">
                    <span className="font-medium text-foreground">{doc.name}</span>
                    <span className="ml-2 text-muted-foreground">
                      {formatDisplayDate(doc.createdAt)}
                    </span>
                  </div>
                  <div className="flex shrink-0 items-center gap-1">
                    <a
                      href={doc.fileUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary underline hover:no-underline"
                    >
                      {t("tenants.detail.documents.open")}
                    </a>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => {
                        setRenameDocId(doc.id);
                        setRenameDocName(doc.name);
                      }}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-destructive hover:text-destructive"
                      onClick={() => setDeleteDocId(doc.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={renameDocId != null}
        onOpenChange={(open) => {
          if (!open) {
            setRenameDocId(null);
            setRenameDocName("");
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("tenants.detail.documents.renameTitle")}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-2 py-2">
            <Label htmlFor="doc-rename">{t("tenants.detail.documents.renameLabel")}</Label>
            <Input
              id="doc-rename"
              value={renameDocName}
              onChange={(e) => setRenameDocName(e.target.value)}
              disabled={renameDocMutation.isPending}
            />
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setRenameDocId(null);
                setRenameDocName("");
              }}
            >
              {t("tenants.detail.documents.cancel")}
            </Button>
            <Button
              type="button"
              onClick={() => {
                if (renameDocId && renameDocName.trim())
                  renameDocMutation.mutate({ docId: renameDocId, name: renameDocName.trim() });
              }}
              disabled={!renameDocName.trim() || renameDocMutation.isPending}
            >
              {renameDocMutation.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : null}
              {t("tenants.detail.documents.save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmationDialog
        open={deleteDocId != null}
        onOpenChange={(open) => !open && setDeleteDocId(null)}
        title={t("tenants.detail.documents.deleteTitle")}
        description={t("tenants.detail.documents.deleteDescription")}
        confirmText={t("tenants.detail.documents.deleteConfirm")}
        variant="destructive"
        onConfirm={() => {
          if (deleteDocId) deleteDocMutation.mutate(deleteDocId);
        }}
        isLoading={deleteDocMutation.isPending}
      />

      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            {t("tenants.detail.support.title")}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {supportMessagesLoading ? (
            <p className="text-sm text-muted-foreground">
              {t("tenants.detail.support.loading")}
            </p>
          ) : supportMessages.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              {t("tenants.detail.support.empty")}
            </p>
          ) : (
            <ul className="space-y-3">
              {supportMessages.map((msg) => (
                <li
                  key={msg.id}
                  className={`rounded-md border px-3 py-2 text-sm ${
                    msg.direction === "support"
                      ? "border-primary/30 bg-primary/5"
                      : "border-border bg-muted/30"
                  }`}
                >
                  <div className="mb-1 font-medium text-muted-foreground">
                    {msg.direction === "support"
                      ? t("tenants.detail.support.fromSupport", {
                          name: msg.authorDisplayName ?? "Support",
                        })
                      : t("tenants.detail.support.fromTenant", {
                          name: msg.authorDisplayName ?? "—",
                        })}
                    {" · "}
                    {formatDisplayDate(msg.createdAt)}
                  </div>
                  <p className="whitespace-pre-wrap text-foreground">
                    {msg.body}
                  </p>
                </li>
              ))}
            </ul>
          )}
          <div className="space-y-2">
            <Label htmlFor="support-message">
              {t("tenants.detail.support.sendLabel")}
            </Label>
            <Textarea
              id="support-message"
              value={supportMessageText}
              onChange={(e) => setSupportMessageText(e.target.value)}
              placeholder={t("tenants.detail.support.placeholder")}
              rows={3}
              disabled={sendSupportMessageMutation.isPending}
              className="resize-none"
            />
            <Button
              type="button"
              size="sm"
              onClick={() => {
                if (supportMessageText.trim())
                  sendSupportMessageMutation.mutate(supportMessageText.trim());
              }}
              disabled={
                !supportMessageText.trim() ||
                sendSupportMessageMutation.isPending
              }
            >
              {sendSupportMessageMutation.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : null}
              {t("tenants.detail.support.send")}
            </Button>
          </div>
        </CardContent>
      </Card>

      {overview.primaryContacts.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <User className="h-4 w-4" />
              <Mail className="h-4 w-4" />
              {t("tenants.detail.primaryContacts")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-1 text-sm">
              {overview.primaryContacts.map((c) => (
                <li key={c.email}>
                  <span className="font-medium text-foreground">{c.displayName}</span>
                  {" — "}
                  <a
                    href={`mailto:${c.email}`}
                    className="text-primary underline hover:no-underline"
                  >
                    {c.email}
                  </a>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}
        </TabsContent>
        <TabsContent value={TAB_FACTURA} className="space-y-4 mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <SlidersHorizontal className="h-4 w-4" />
                {t("tenants.edit.dgiEnvLabel")}
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                {t("tenants.edit.dgiEnvHelp")}
              </p>
            </CardHeader>
            <CardContent>
              <Select
                value={
                  overview.dgiEnv === "prod"
                    ? "prod"
                    : overview.dgiEnv === "test"
                      ? "test"
                      : "default"
                }
                onValueChange={(value) => {
                  const dgiEnv =
                    value === "default"
                      ? null
                      : (value as "test" | "prod");
                  setDgiEnvMutation.mutate({ tenantId: id!, dgiEnv });
                }}
                disabled={setDgiEnvMutation.isPending}
              >
                <SelectTrigger className="w-[200px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="default">
                    {t("tenants.edit.dgiEnvDefault")}
                  </SelectItem>
                  <SelectItem value="test">
                    {t("tenants.edit.dgiEnvTest")}
                  </SelectItem>
                  <SelectItem value="prod">
                    {t("tenants.edit.dgiEnvProd")}
                  </SelectItem>
                </SelectContent>
              </Select>
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle className="text-base text-foreground">
                {t("settings.cfe.title")}
              </CardTitle>
              <CardDescription>{t("settings.cfe.description")}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <p className="text-sm font-medium text-foreground">
                  {t("settings.cfe.importGroupLabel", "Importar")}
                </p>
                <p className="text-sm text-muted-foreground">
                  {t("settings.cfe.importHint", "Subí archivos XML o ZIP desde DGI u otro sistema.")}
                </p>
                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setImportEmittedOpen(true)}
                    className="transition-colors duration-150 hover:bg-muted/80 hover:text-foreground"
                  >
                    <Upload className="mr-2 h-4 w-4 shrink-0" />
                    {t("settings.cfe.importEmitted")}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setImportRecibidasOpen(true)}
                    className="transition-colors duration-150 hover:bg-muted/80 hover:text-foreground"
                  >
                    <Upload className="mr-2 h-4 w-4 shrink-0" />
                    {t("settings.cfe.importRecibidas")}
                  </Button>
                </div>
              </div>
              <div className="border-t pt-4">
                <div className="space-y-2">
                  <p className="text-sm font-medium text-foreground">
                    {t("settings.cfe.exportGroupLabel", "Exportar")}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {t("settings.cfe.exportHint", "Se descarga un ZIP con los CFE (emitidos aceptados o recibidos importados).")}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      disabled={exportEmittedLoading}
                      className="transition-colors duration-150 hover:bg-muted/80 hover:text-foreground"
                      onClick={async () => {
                        if (!id) return;
                        setExportEmittedLoading(true);
                        try {
                          await downloadExportEmittedCfesForTenant(id);
                          toast.success(
                            t("settings.cfe.exportEmittedSuccess", "CFE emitidos descargados.")
                          );
                        } catch {
                          toast.error(
                            t("settings.cfe.exportEmittedError", "No se pudo descargar el archivo.")
                          );
                        } finally {
                          setExportEmittedLoading(false);
                        }
                      }}
                    >
                      {exportEmittedLoading ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 shrink-0 animate-spin" />
                          {t("settings.cfe.exportEmitted")}
                        </>
                      ) : (
                        <>
                          <Download className="mr-2 h-4 w-4 shrink-0" />
                          {t("settings.cfe.exportEmitted")}
                        </>
                      )}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      disabled={exportRecibidasLoading}
                      className="transition-colors duration-150 hover:bg-muted/80 hover:text-foreground"
                      onClick={async () => {
                        if (!id) return;
                        setExportRecibidasLoading(true);
                        try {
                          await downloadExportRecibidasCfesForTenant(id);
                          toast.success(
                            t("settings.cfe.exportRecibidasSuccess", "CFE recibidos descargados.")
                          );
                        } catch {
                          toast.error(
                            t("settings.cfe.exportRecibidasError", "No se pudo descargar el archivo.")
                          );
                        } finally {
                          setExportRecibidasLoading(false);
                        }
                      }}
                    >
                      {exportRecibidasLoading ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 shrink-0 animate-spin" />
                          {t("settings.cfe.exportRecibidas")}
                        </>
                      ) : (
                        <>
                          <Download className="mr-2 h-4 w-4 shrink-0" />
                          {t("settings.cfe.exportRecibidas")}
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
          <TenantCaeCard tenantId={id!} />
          <DgiCertificateCard
            tenantId={id!}
            rut={overview.rut}
            onSuccess={() =>
              queryClient.invalidateQueries({ queryKey: tenantKeys.overview(id!) })
            }
          />
          <TenantFiscaloConfigCard tenantId={id!} overview={overview} />
          <TenantHomologacionCard tenantId={id!} overview={overview} />
          <TenantOnboardingAutoFlowCard tenantId={id!} overview={overview} />
          <TenantBajaCard tenantId={id!} />
        </TabsContent>
        <TabsContent value={TAB_FISCAL} className="space-y-4 mt-4">
          <TenantFiscalProfileCard tenantId={id!} />
        </TabsContent>
      </Tabs>

      <ImportEmittedCfeDialog
        tenantId={id!}
        tenantName={displayName}
        open={importEmittedOpen}
        onOpenChange={setImportEmittedOpen}
      />
      <ImportRecibidasCfeDialog
        tenantId={id!}
        tenantName={displayName}
        open={importRecibidasOpen}
        onOpenChange={setImportRecibidasOpen}
      />

      <ConfirmationDialog
        open={deleteInactiveOpen}
        onOpenChange={(open) => !open && setDeleteInactiveOpen(false)}
        title={t("tenants.detail.deleteInactiveSetup.dialogTitle")}
        description={t("tenants.detail.deleteInactiveSetup.dialogDescription", {
          name: displayName,
        })}
        confirmText={t("tenants.detail.deleteInactiveSetup.confirm")}
        cancelText={t("tenants.detail.deleteInactiveSetup.cancel")}
        loadingText={t("tenants.detail.deleteInactiveSetup.loading")}
        variant="destructive"
        onConfirm={() => deleteInactiveMutation.mutate()}
        isLoading={deleteInactiveMutation.isPending}
      />
    </div>
  );
}
